import { useEffect, useState, useRef } from 'react';
import { Form, Card, notification, Tooltip } from 'antd';
import { Authorize, Button } from 'app/atoms';
import GlobalSearchBar from 'app/atoms/GlobalSearchBar';
import BannerFields, { BannerFieldsRef } from './BannerFields';
import { useTranslation } from 'react-i18next';
import { customizeRequiredMark } from 'utils/fieldConfig';
import { BANNER_CONFIG } from 'configs/routeNames';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import FormSkeleton from 'app/atoms/FormSkeleton';
import EnhancedTable from 'app/molecules/EnhancedTable';
import {
  createBanner,
  getSingleBanner,
  updateBannerTranslation,
  updateBannerTranslationById,
  deleteBannerTranslation,
  updateBanner,
} from './services';
import { getErrorMessage, ErrorMessage } from 'utils/axios';
import { useUserInfo } from 'app/hooks/useUserInfo';
import dayjs from 'dayjs';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { checkCommonFieldsChanged } from './utils';
import { DATE_FORMAT_FOR_FILTER, TIME_FORMAT_FOR_PAYLOAD } from 'configs/constants';
import { BannerTranslationWithId, BannerFormValues } from './types';
import { NotificationProps } from 'utils/types';
import { PermissionActions } from '../Auth/types';
import { PERMISSIONS_SUBJECTS } from '../Auth/constants';
import { usePermissions } from 'app/hooks/usePermissions';

const BannerForm = () => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filteredColumnsInfo, setFilteredColumnsInfo] = useState<Record<string, string>>({});
  const { data: currentUser } = useUserInfo();
  const agencyId = currentUser?.agency?.id;
  const [selectedLang, setSelectedLang] = useState(i18n.language);
  const [titleCount, setTitleCount] = useState(0);
  const bannerFieldsRef = useRef<BannerFieldsRef>(null);

  const { can } = usePermissions();

  const columns = [
    {
      title: t('banner.table.dataColumns.language'),
      dataIndex: 'languageCode',
      searchable: false,
      width: 100,
      ellipsis: true,
    },
    {
      title: t('banner.table.dataColumns.title'),
      dataIndex: 'translatedTitle',
      searchable: false,
      width: 300,
      className: 'relative',
      render: (title: string) => (
        <div className="truncate-text">
          <Tooltip placement="topLeft" title={title ?? ''}>
            <span>{title ?? t('N/A')}</span>
          </Tooltip>
        </div>
      ),
    },
    {
      title: t('banner.table.dataColumns.content'),
      dataIndex: 'translatedContent',
      searchable: false,
      width: 400,
      className: 'relative',
      render: (content: string) => {
        if (!content) return '-';
        // Create a temporary div to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;

        const text = tempDiv.textContent || '';

        return (
          <div className="truncate-text">
            <Tooltip placement="topLeft" title={text ?? ''}>
              <span>{text ?? t('N/A')}</span>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: t('banner.table.dataColumns.translatedImage'),
      dataIndex: 'translatedImageUrl',
      width: 250,
      className: 'relative',
      render: (imageUrl: string) =>
        imageUrl ? (
          <img src={imageUrl} alt={t('banner.table.imageAlt')} className="object-cover w-[220px] h-[30px] rounded-lg" />
        ) : (
          '-'
        ),
      searchable: false,
    },
  ];

  const {
    data: bannerData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['banner', id],
    queryFn: () => (id ? getSingleBanner(id) : undefined),
    enabled: Boolean(id),
  });

  const createBannerMutation = useMutation({
    mutationKey: ['create-banner'],
    mutationFn: createBanner,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      notification.success({
        message: t('apiSuccessMessages.createBanner'),
        ...NotificationProps,
      });
      navigate(`/${BANNER_CONFIG}`);
    },
    onError: (error: ErrorMessage) => {
      notification.error({
        message: getErrorMessage(error),
        ...NotificationProps,
      });
    },
  });

  const updateBannerMutation = useMutation({
    mutationKey: ['update-banner'],
    mutationFn: ({ id, formData }: { id: string; formData: FormData }) => updateBanner(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banner', id] });
      notification.success({
        message: t('apiSuccessMessages.updateBanner'),
        ...NotificationProps,
      });
    },
    onError: (error: ErrorMessage) => {
      notification.error({
        message: getErrorMessage(error),
        ...NotificationProps,
      });
    },
  });

  const createBannerLanguageMutation = useMutation({
    mutationKey: ['update-banner-translation'],
    mutationFn: ({ id, formData }: { id: string; formData: FormData }) => updateBannerTranslation(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banner', id] });
      notification.success({
        message: t('apiSuccessMessages.createBannerTranslation'),
        ...NotificationProps,
      });
    },
    onError: (error: ErrorMessage) => {
      notification.error({
        message: getErrorMessage(error),
        ...NotificationProps,
      });
    },
  });

  const updateBannerLanguageMutation = useMutation({
    mutationKey: ['update-banner-translation-by-id'],
    mutationFn: ({ translationId, formData }: { translationId: string; formData: FormData }) =>
      updateBannerTranslationById(translationId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banner', id] });
      notification.success({
        message: t('apiSuccessMessages.updateBannerTranslation'),
        ...NotificationProps,
      });
    },
    onError: (error: ErrorMessage) => {
      notification.error({
        message: getErrorMessage(error),
        ...NotificationProps,
      });
    },
  });

  const deleteBannerLanguageMutation = useMutation({
    mutationKey: ['delete-banner-translation'],
    mutationFn: (translationId: string) => deleteBannerTranslation(translationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banner', id] });
      notification.success({
        message: t('apiSuccessMessages.deleteBannerTranslation'),
        ...NotificationProps,
      });
    },
    onError: (error: ErrorMessage) => {
      notification.error({
        message: getErrorMessage(error),
        ...NotificationProps,
      });
    },
  });

  useEffect(() => {
    if (!id || isFetching || !bannerData) return;

    // Find translation for selectedLang
    const translation = Array.isArray(bannerData.translations)
      ? bannerData.translations.find((tr: BannerTranslationWithId) => tr.languageCode === selectedLang)
      : undefined;

    // Set common fields that don't depend on language
    const commonFields = {
      startDate: bannerData.displayStartDate ? dayjs(bannerData.displayStartDate) : undefined,
      endDate: bannerData.displayEndDate ? dayjs(bannerData.displayEndDate) : undefined,
      link: bannerData.linkUrl,
    };

    const translatedTitle = translation?.translatedTitle || '';
    const translatedContent = translation?.translatedContent || '';
    const translatedImageUrl = translation?.translatedImageUrl || '';

    const formValues = {
      ...commonFields,
      title: translatedTitle,
      content: translatedContent,
      imageUrl: translatedImageUrl,
      image: translatedImageUrl ? [{ url: translatedImageUrl, name: 'Banner Image' }] : undefined,
    };

    form.setFieldsValue(formValues);

    // Update refs and state
    bannerFieldsRef.current?.setContent(translatedContent);
    bannerFieldsRef.current?.setImage(translatedImageUrl || null);
    setTitleCount(translatedTitle.length);
  }, [id, form, isFetching, selectedLang, bannerData]);

  const handleBackToList = () => {
    queryClient.setQueryData(['header-props'], {
      selectedMenuItem: t('sideMenu.banners'),
    });

    if (window.history.state && window.history.state.idx > 0) {
      // using the browser route history and go back from where you came from - stack memory
      navigate(-1);
    } else {
      // default case
      navigate(`/${BANNER_CONFIG}`);
    }
  };

  useEffect(() => {
    queryClient.setQueryData(['header-props'], {
      selectedMenuItem: t('sideMenu.banners'),
      onBack: handleBackToList,
      detailTitle: id ? `${t('banner.form.editHeader')} - #${id}` : t('banner.form.addHeader'),
      isLoading: isLoading,
    });
  }, [queryClient, t, id, isLoading]);

  const appendImageToFormData = (formData: FormData, values: BannerFormValues) => {
    if (values.image && values.image.length > 0) {
      const file = values.image[0]?.originFileObj;
      if (file && file instanceof Blob) {
        formData.append('image', file);
      }
    }
  };

  const createTranslationFormData = (values: BannerFormValues) => {
    const formData = new FormData();
    const translation = {
      languageCode: selectedLang,
      translatedTitle: values.title,
      translatedContent: values.content,
      translatedImageUrl: values.imageUrl,
    };
    formData.append('translation', JSON.stringify(translation));
    appendImageToFormData(formData, values);
    return formData;
  };

  const handleSubmit = async (values: BannerFormValues) => {
    if (id) {
      const hasCommonFieldsChanged = checkCommonFieldsChanged(values, id, bannerData);
      if (hasCommonFieldsChanged) {
        // Create formData for common fields update
        const formData = new FormData();
        const otherTranslations = bannerData?.translations
          ?.filter((tr: BannerTranslationWithId) => tr.languageCode !== selectedLang)
          .map(({ id: _id, ...rest }: BannerTranslationWithId) => rest);

        const newBannerData = {
          agencyId: Number(agencyId),
          translations: [
            ...otherTranslations,
            {
              languageCode: selectedLang,
              translatedTitle: values?.title ?? '',
              translatedContent: values?.content ?? '',
            },
          ],
          displayEndDate: `${dayjs(values.endDate).format(TIME_FORMAT_FOR_PAYLOAD)}Z`,
          displayStartDate: `${dayjs(values.startDate).format(TIME_FORMAT_FOR_PAYLOAD)}Z`,
          language: selectedLang,
          linkUrl: values?.link ?? '',
          title: values?.title ?? '',
          content: values?.content ?? '',
        };
        formData.append('banner', JSON.stringify(newBannerData));
        appendImageToFormData(formData, values);
        updateBannerMutation.mutate({ id, formData });
        return;
      }

      // Find existing translation for selected language
      const existingTranslation = bannerData?.translations?.find(
        (tr: BannerTranslationWithId) => tr.languageCode === selectedLang,
      );
      const formData = createTranslationFormData(values);

      if (existingTranslation) {
        // Update existing translation using translation ID
        updateBannerLanguageMutation.mutate({ translationId: existingTranslation.id, formData });
      } else {
        // Create new translation for the banner
        createBannerLanguageMutation.mutate({ id, formData });
      }
    } else {
      // Create new banner
      const formData = new FormData();
      const bannerData = {
        agencyId: Number(agencyId),
        translations: [
          {
            languageCode: selectedLang,
            translatedTitle: values?.title ?? '',
            translatedContent: values?.content ?? '',
          },
        ],
        displayEndDate: dayjs(values.endDate).format(DATE_FORMAT_FOR_FILTER),
        displayStartDate: dayjs(values.startDate).format(DATE_FORMAT_FOR_FILTER),
        linkUrl: values.link ?? '',
        title: values.title ?? '',
        content: values.content ?? '',
      };
      formData.append('banner', JSON.stringify(bannerData));
      appendImageToFormData(formData, values);
      createBannerMutation.mutate(formData);
    }
  };

  const handleEditTranslation = (record: BannerTranslationWithId) => {
    setSelectedLang(record.languageCode);
    // Scroll to top of the page
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleDeleteTranslation = (record: BannerTranslationWithId) => {
    deleteBannerLanguageMutation.mutate(record.id.toString());
  };

  const actions = [
    ...(can(PermissionActions.EDIT_BANNERS, PERMISSIONS_SUBJECTS.BANNERS)
      ? [
          {
            key: 'edit',
            label: () => t('banner.table.actions.edit'),
            icon: <EditOutlined />,
            onClick: handleEditTranslation,
          },
        ]
      : []),
    ...(can(PermissionActions.EDIT_BANNERS, PERMISSIONS_SUBJECTS.BANNERS)
      ? [
          {
            key: 'delete',
            label: () => t('banner.table.actions.delete'),
            icon: <DeleteOutlined />,
            onClick: handleDeleteTranslation,
            danger: true,
          },
        ]
      : []),
  ];

  return (
    <>
      <GlobalSearchBar />
      <Card className="mb-6">
        {isLoading || isFetching ? (
          <FormSkeleton rows={4} cols={2} showSubmitButton />
        ) : (
          <Form form={form} requiredMark={customizeRequiredMark} onFinish={handleSubmit} layout="vertical">
            <BannerFields
              ref={bannerFieldsRef}
              form={form}
              selectedLang={selectedLang}
              onLangChange={setSelectedLang}
              titleCount={titleCount}
              onTitleChange={setTitleCount}
            >
              <Authorize
                requiredPermissions={[
                  { action: PermissionActions.EDIT_BANNERS, subject: PERMISSIONS_SUBJECTS.BANNERS },
                ]}
              >
                <div className="flex justify-end">
                  <Button
                    type="primary"
                    size="large"
                    data-testid="banner-form-save-button"
                    htmlType="submit"
                    loading={
                      createBannerMutation.isPending ||
                      createBannerLanguageMutation.isPending ||
                      updateBannerMutation.isPending ||
                      updateBannerLanguageMutation.isPending
                    }
                  >
                    {t('banner.form.saveButton')}
                  </Button>
                </div>
              </Authorize>
            </BannerFields>
          </Form>
        )}
      </Card>
      {bannerData?.translations && bannerData?.translations?.length > 0 && (
        <Card>
          <EnhancedTable
            columns={columns}
            dataSource={bannerData?.translations || []}
            pagination={false}
            rowKey="id"
            loading={isLoading}
            showSearch={false}
            showSort={true}
            showView={false}
            showEdit={false}
            showRemove={false}
            showMenu={true}
            sortAndFilterInfo={{}}
            onSearch={() => {}}
            onEdit={() => {}}
            onView={() => {}}
            onRemove={() => {}}
            onChange={() => {}}
            setFilteredColumnsInfo={setFilteredColumnsInfo}
            filteredColumnsInfo={filteredColumnsInfo}
            actions={actions}
          />
        </Card>
      )}
    </>
  );
};

export default BannerForm;
